/**
 * Fail-closed runtime guard for LLM-6 coaching action proposals.
 * Validates structure only — no training logic or mutation authority.
 */

const MAX_PROPOSAL_REF_LENGTH = 32;
const MAX_SUMMARY_LENGTH = 1000;
const MAX_RATIONALE_LENGTH = 1500;
const MAX_STRING_LIST_ITEMS = 12;
const MAX_STRING_LIST_ITEM_LENGTH = 500;

export const PROPOSAL_PARSE_STATE = {
  ABSENT: "ABSENT",
  VALID: "VALID",
  MALFORMED: "MALFORMED",
};

export const PROPOSAL_STATUS = {
  READY_FOR_CONFIRMATION: "READY_FOR_CONFIRMATION",
  NEEDS_INFORMATION: "NEEDS_INFORMATION",
};

export const PROPOSAL_EXECUTION_STATE = {
  NOT_EXECUTED: "NOT_EXECUTED",
};

/** Mirrors backend ProposableCoachingIntent — unknown values degrade gracefully. */
export const KNOWN_PROPOSABLE_INTENTS = new Set([
  "UPDATE_PROFILE",
  "UPDATE_AVAILABILITY",
  "UPDATE_GOAL",
  "UPDATE_TARGET_EVENT",
  "UPDATE_EQUIPMENT",
  "UPDATE_SENSOR_STATUS",
  "SYNC_ACTIVITIES",
  "REQUEST_PLAN_ADAPTATION",
  "ADJUST_PLANNED_SESSION",
  "MOVE_PLANNED_SESSION",
  "UPDATE_FTP",
  "REPLAN_REMAINING_BLOCK",
]);

const INTENT_LABELS = {
  UPDATE_PROFILE: "Profile update",
  UPDATE_AVAILABILITY: "Availability update",
  UPDATE_GOAL: "Training goal update",
  UPDATE_TARGET_EVENT: "Target event update",
  UPDATE_EQUIPMENT: "Equipment update",
  UPDATE_SENSOR_STATUS: "Sensor status update",
  SYNC_ACTIVITIES: "Activity sync",
  REQUEST_PLAN_ADAPTATION: "Plan adaptation review",
  ADJUST_PLANNED_SESSION: "Suggested plan change",
  MOVE_PLANNED_SESSION: "Suggested schedule change",
  UPDATE_AVAILABILITY: "Suggested availability change",
  UPDATE_FTP: "Suggested FTP update",
  REPLAN_REMAINING_BLOCK: "Plan rebuild ready for review",
};

const isNonEmptyString = (value, maxLength) =>
  typeof value === "string"
  && value.trim().length > 0
  && value.trim().length <= maxLength;

const normalizeStringList = (raw, maxItems, maxItemLength) => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = [];
  for (const item of raw.slice(0, maxItems)) {
    if (typeof item !== "string") {
      continue;
    }
    const trimmed = item.trim();
    if (trimmed.length > 0 && trimmed.length <= maxItemLength) {
      out.push(trimmed);
    }
  }
  return out;
};

/**
 * @param {unknown} raw
 * @returns {{
 *   parseState: string,
 *   proposal: null | {
 *     proposalRef: string,
 *     intent: string,
 *     intentLabel: string,
 *     origin: string,
 *     status: string,
 *     summary: string,
 *     rationale: string,
 *     proposedFields: string[],
 *     missingInformation: string[],
 *     confirmationRequired: boolean,
 *     executionState: string,
 *     isKnownIntent: boolean,
 *   },
 * }}
 */
export const normalizeActionProposal = (raw) => {
  if (raw == null) {
    return { parseState: PROPOSAL_PARSE_STATE.ABSENT, proposal: null };
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return { parseState: PROPOSAL_PARSE_STATE.MALFORMED, proposal: null };
  }

  const proposalRef = typeof raw.proposal_ref === "string"
    ? raw.proposal_ref.trim()
    : "";
  const intent = typeof raw.intent === "string" ? raw.intent.trim() : "";
  const origin = typeof raw.origin === "string" ? raw.origin.trim() : "";
  const status = typeof raw.status === "string" ? raw.status.trim() : "";
  const summary = typeof raw.summary === "string" ? raw.summary.trim() : "";
  const rationale = typeof raw.rationale === "string" ? raw.rationale.trim() : "";

  if (
    !isNonEmptyString(proposalRef, MAX_PROPOSAL_REF_LENGTH)
    || !isNonEmptyString(intent, 64)
    || !isNonEmptyString(origin, 64)
    || !isNonEmptyString(status, 64)
    || !isNonEmptyString(summary, MAX_SUMMARY_LENGTH)
    || !isNonEmptyString(rationale, MAX_RATIONALE_LENGTH)
  ) {
    return { parseState: PROPOSAL_PARSE_STATE.MALFORMED, proposal: null };
  }

  const executionState = typeof raw.execution_state === "string"
    ? raw.execution_state.trim()
    : PROPOSAL_EXECUTION_STATE.NOT_EXECUTED;

  return {
    parseState: PROPOSAL_PARSE_STATE.VALID,
    proposal: {
      proposalRef,
      intent,
      intentLabel: INTENT_LABELS[intent] || "Coaching action",
      origin,
      status,
      summary,
      rationale,
      proposedFields: normalizeStringList(
        raw.proposed_fields,
        MAX_STRING_LIST_ITEMS,
        MAX_STRING_LIST_ITEM_LENGTH,
      ),
      missingInformation: normalizeStringList(
        raw.missing_information,
        MAX_STRING_LIST_ITEMS,
        MAX_STRING_LIST_ITEM_LENGTH,
      ),
      confirmationRequired: raw.confirmation_required === true,
      executionState,
      isKnownIntent: KNOWN_PROPOSABLE_INTENTS.has(intent),
    },
  };
};

/**
 * Athlete-facing primary action label — must match actual backend semantics.
 * @param {string} intent
 */
export const getConfirmationButtonLabel = (intent) => {
  switch (intent) {
    case "REQUEST_PLAN_ADAPTATION":
      return "Apply changes";
    case "ADJUST_PLANNED_SESSION":
    case "MOVE_PLANNED_SESSION":
      return "Apply change";
    case "UPDATE_AVAILABILITY":
      return "Apply changes";
    case "UPDATE_FTP":
      return "Apply FTP";
    case "SYNC_ACTIVITIES":
      return "Confirm sync";
    default:
      return "Confirm";
  }
};

export const getRejectButtonLabel = (intent) => {
  switch (intent) {
    case "ADJUST_PLANNED_SESSION":
    case "MOVE_PLANNED_SESSION":
    case "REQUEST_PLAN_ADAPTATION":
    case "UPDATE_AVAILABILITY":
      return "Keep current plan";
    case "UPDATE_FTP":
      return "Keep current FTP";
    default:
      return "Not now";
  }
};

/**
 * Whether a completed material action mutates the active training plan calendar.
 * Step 1: REQUEST_PLAN_ADAPTATION evaluates only; no proposable intent applies
 * calendar mutations directly.
 * @param {string} intent
 * @param {boolean} materialActionTaken
 */
export const affectsActiveTrainingPlan = (intent, materialActionTaken) => {
  if (!materialActionTaken) {
    return false;
  }
  if (
    intent === "ADJUST_PLANNED_SESSION"
    || intent === "MOVE_PLANNED_SESSION"
    || intent === "REQUEST_PLAN_ADAPTATION"
    || intent === "UPDATE_AVAILABILITY"
  ) {
    return true;
  }
  return false;
};

/**
 * Success copy after material_action_taken=true. Never claims plan was updated
 * unless affectsActiveTrainingPlan is true.
 * @param {string} intent
 * @param {boolean} materialActionTaken
 */
export const getMaterialActionSuccessMessage = (intent, materialActionTaken) => {
  if (!materialActionTaken) {
    return null;
  }
  if (intent === "ADJUST_PLANNED_SESSION") {
    return "Your upcoming workout has been updated.";
  }
  if (intent === "REQUEST_PLAN_ADAPTATION") {
    return "The rest of this week has been updated.";
  }
  if (intent === "MOVE_PLANNED_SESSION") {
    return "Your workout has been rescheduled.";
  }
  if (intent === "UPDATE_AVAILABILITY") {
    return "Your availability and training schedule have been updated.";
  }
  if (intent === "UPDATE_FTP") {
    return "Your FTP has been updated.";
  }
  if (affectsActiveTrainingPlan(intent, materialActionTaken)) {
    return "Your training plan was updated.";
  }
  switch (intent) {
    case "SYNC_ACTIVITIES":
      return "Activity sync completed.";
    default:
      return "Action completed.";
  }
};

const normalizePreviewSide = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const dayLabel = typeof raw.day_label === "string" ? raw.day_label.trim() : "";
  const sessionTitle = typeof raw.session_title === "string"
    ? raw.session_title.trim()
    : "";
  const difficultyLabel = typeof raw.difficulty_label === "string"
    ? raw.difficulty_label.trim()
    : "";
  if (!dayLabel || !sessionTitle || !difficultyLabel) {
    return null;
  }
  const sport = typeof raw.sport === "string" ? raw.sport.trim() : null;
  const intensitySummary = typeof raw.intensity_summary === "string"
    ? raw.intensity_summary.trim()
    : null;
  const durationMinutes = typeof raw.duration_minutes === "number"
    && Number.isFinite(raw.duration_minutes)
    ? raw.duration_minutes
    : null;
  const scheduledDate = typeof raw.scheduled_date === "string"
    ? raw.scheduled_date.trim()
    : null;
  return {
    dayLabel,
    scheduledDate,
    sport,
    sessionTitle,
    durationMinutes,
    difficultyLabel,
    intensitySummary,
  };
};

/**
 * @param {unknown} raw
 * @returns {null | {
 *   intent: string,
 *   before: ReturnType<typeof normalizePreviewSide>,
 *   after: ReturnType<typeof normalizePreviewSide>,
 *   why: string,
 * }}
 */
const normalizeSessionChangeEntry = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const dayLabel = typeof raw.day_label === "string" ? raw.day_label.trim() : "";
  const why = typeof raw.why === "string" ? raw.why.trim() : "";
  const before = normalizePreviewSide(raw.before);
  const after = normalizePreviewSide(raw.after);
  if (!dayLabel || !why || !before || !after) {
    return null;
  }
  const sport = typeof raw.sport === "string" ? raw.sport.trim() : null;
  const scheduledDate = typeof raw.scheduled_date === "string"
    ? raw.scheduled_date.trim()
    : null;
  return {
    dayLabel,
    scheduledDate,
    sport,
    before,
    after,
    why,
  };
};

const normalizeAvailabilityChangeEntry = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const dayLabel = typeof raw.day_label === "string" ? raw.day_label.trim() : "";
  const beforeLabel = typeof raw.before_label === "string" ? raw.before_label.trim() : "";
  const afterLabel = typeof raw.after_label === "string" ? raw.after_label.trim() : "";
  const why = typeof raw.why === "string" ? raw.why.trim() : "";
  if (!dayLabel || !beforeLabel || !afterLabel || !why) {
    return null;
  }
  return {
    dayLabel,
    scheduledDate: typeof raw.scheduled_date === "string" ? raw.scheduled_date.trim() : null,
    beforeLabel,
    afterLabel,
    why,
  };
};

const normalizeFtpPreview = (raw) => {
  const intent = typeof raw.intent === "string" ? raw.intent.trim() : "";
  const why = typeof raw.why === "string" ? raw.why.trim() : "";
  const proposed = raw.proposed_ftp_watts;
  if (intent !== "UPDATE_FTP" || !why || !Number.isFinite(proposed)) {
    return null;
  }
  const current = Number.isFinite(raw.current_ftp_watts) ? raw.current_ftp_watts : null;
  const evidenceSummary = typeof raw.evidence_summary === "string"
    ? raw.evidence_summary.trim()
    : null;
  return {
    kind: "ftp",
    intent,
    headline: typeof raw.headline === "string" ? raw.headline.trim() : "FTP update",
    currentFtpWatts: current,
    proposedFtpWatts: proposed,
    changeWatts: Number.isFinite(raw.change_watts) ? raw.change_watts : null,
    changePercent: Number.isFinite(raw.change_percent) ? raw.change_percent : null,
    currentWkg: Number.isFinite(raw.current_wkg) ? raw.current_wkg : null,
    proposedWkg: Number.isFinite(raw.proposed_wkg) ? raw.proposed_wkg : null,
    why,
    evidenceSummary,
    sourceMode: typeof raw.source_mode === "string" ? raw.source_mode.trim() : "",
  };
};

export const normalizePlanChangePreview = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const intent = typeof raw.intent === "string" ? raw.intent.trim() : "";
  const headline = typeof raw.headline === "string" ? raw.headline.trim() : null;

  const ftpPreview = normalizeFtpPreview(raw);
  if (ftpPreview) {
    return ftpPreview;
  }

  if (Array.isArray(raw.availability_changes)) {
    const availabilityChanges = raw.availability_changes
      .map((entry) => normalizeAvailabilityChangeEntry(entry))
      .filter(Boolean);
    const changes = Array.isArray(raw.changes)
      ? raw.changes.map((entry) => normalizeSessionChangeEntry(entry)).filter(Boolean)
      : [];
    if (intent !== "UPDATE_AVAILABILITY" || availabilityChanges.length === 0) {
      return null;
    }
    return { kind: "schedule", intent, headline, availabilityChanges, changes };
  }

  if (Array.isArray(raw.changes)) {
    const changes = raw.changes
      .map((entry) => normalizeSessionChangeEntry(entry))
      .filter(Boolean);
    if (intent !== "REQUEST_PLAN_ADAPTATION" || changes.length === 0) {
      return null;
    }
    return { kind: "multi", intent, headline, changes };
  }

  const why = typeof raw.why === "string" ? raw.why.trim() : "";
  const before = normalizePreviewSide(raw.before);
  const after = normalizePreviewSide(raw.after);
  if (
    !why
    || !before
    || !after
    || (intent !== "ADJUST_PLANNED_SESSION" && intent !== "MOVE_PLANNED_SESSION")
  ) {
    return null;
  }
  return { kind: "single", intent, before, after, why };
};

export const normalizeTrainingPlanDraft = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  if (raw.status !== "VALIDATED_DRAFT") {
    return null;
  }
  const draftRef = typeof raw.draft_ref === "string" ? raw.draft_ref.trim() : "";
  if (!draftRef) {
    return null;
  }
  return {
    status: raw.status,
    draftRef,
    draft: raw.draft || null,
    reply: typeof raw.reply === "string" ? raw.reply.trim() : null,
    raw,
  };
};

export const TRAINING_PLAN_DRAFT_READY_EVENT = "endurava:training-plan-draft-ready";

export const dispatchTrainingPlanDraftReady = (draft) => {
  if (!draft?.raw) {
    return;
  }
  window.dispatchEvent(new CustomEvent(TRAINING_PLAN_DRAFT_READY_EVENT, {
    detail: draft.raw,
  }));
};

export const CONFIRM_REPLY = "Yes";
export const REJECT_REPLY = "No";
